/**
 * Exponential backoff retry utility for Supabase requests.
 * Handles transient network errors gracefully without overwhelming the server.
 */

interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  shouldRetry?: (error: unknown) => boolean;
  onRetry?: (attempt: number, error: unknown, delayMs: number) => void;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  shouldRetry: isRetryableError,
  onRetry: (attempt, error, delayMs) => {
    console.warn(`[SUPABASE-RETRY] Attempt ${attempt} failed, retrying in ${delayMs}ms`, error);
  },
};

/**
 * Determines if an error is transient and should trigger a retry.
 */
export function isRetryableError(error: unknown): boolean {
  if (!error) return false;

  // Network errors
  if (error instanceof TypeError && error.message?.includes('Failed to fetch')) {
    return true;
  }

  // Supabase/PostgREST error codes that are retryable
  if (typeof error === 'object' && error !== null) {
    const err = error as { code?: string; message?: string; status?: number };
    
    // Network-related errors
    if (err.message?.includes('Failed to fetch')) return true;
    if (err.message?.includes('NetworkError')) return true;
    if (err.message?.includes('network')) return true;
    if (err.code === 'PGRST301') return true; // Connection error
    
    // Rate limiting
    if (err.status === 429) return true;
    
    // Server errors (5xx) - transient
    if (err.status && err.status >= 500 && err.status < 600) return true;
  }

  return false;
}

/**
 * Calculates delay with exponential backoff and jitter.
 */
function calculateDelay(attempt: number, baseDelayMs: number, maxDelayMs: number): number {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = baseDelayMs * Math.pow(2, attempt);
  
  // Add jitter (±25%) to prevent thundering herd
  const jitter = exponentialDelay * 0.25 * (Math.random() - 0.5);
  
  // Cap at maxDelay
  return Math.min(exponentialDelay + jitter, maxDelayMs);
}

/**
 * Delays execution for specified milliseconds.
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wraps an async function with exponential backoff retry logic.
 * 
 * @example
 * const result = await withRetry(async () => {
 *   const { data, error } = await supabase.from('table').select('*');
 *   if (error) throw error;
 *   return data;
 * });
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: unknown;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      // Check if we're offline before attempting
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw new Error('Device is offline');
      }

      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if we've exhausted attempts
      if (attempt >= opts.maxRetries) {
        break;
      }

      // Don't retry if the error isn't retryable
      if (!opts.shouldRetry(error)) {
        break;
      }

      // Calculate delay and wait
      const delayMs = calculateDelay(attempt, opts.baseDelayMs, opts.maxDelayMs);
      opts.onRetry(attempt + 1, error, delayMs);
      await delay(delayMs);
    }
  }

  throw lastError;
}

/**
 * Wraps a Supabase query result with retry logic.
 * Automatically handles the { data, error } pattern.
 * 
 * @example
 * const data = await withSupabaseRetry(
 *   () => supabase.from('table').select('*')
 * );
 */
export async function withSupabaseRetry<T>(
  queryFn: () => PromiseLike<{ data: T | null; error: unknown }>,
  options: RetryOptions = {}
): Promise<T | null> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  return withRetry(async () => {
    const { data, error } = await queryFn();
    
    if (error) {
      // If it's a retryable error, throw to trigger retry
      if (opts.shouldRetry(error)) {
        throw error;
      }
      // Non-retryable errors are logged but not retried
      console.error('[SUPABASE-RETRY] Non-retryable error:', error);
      throw error;
    }

    return data;
  }, {
    ...opts,
    onRetry: (attempt, error, delayMs) => {
      console.warn(
        `[SUPABASE-RETRY] Query failed (attempt ${attempt}/${opts.maxRetries}), ` +
        `retrying in ${Math.round(delayMs)}ms...`
      );
    },
  });
}

/**
 * Silent retry wrapper that returns null on failure instead of throwing.
 * Useful for non-critical background queries.
 */
export async function withSilentRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T | null> {
  try {
    return await withRetry(fn, {
      ...options,
      onRetry: (attempt, _error, delayMs) => {
        console.log(`[SUPABASE-RETRY] Silent retry ${attempt}, waiting ${Math.round(delayMs)}ms`);
      },
    });
  } catch (error) {
    // Log as warning instead of error for silent failures
    if (isRetryableError(error)) {
      console.warn('[SUPABASE-RETRY] All retries failed (silent mode):', error);
    } else {
      console.error('[SUPABASE-RETRY] Non-retryable error (silent mode):', error);
    }
    return null;
  }
}
